// Font loader for CJK (Chinese/Japanese/Korean) support in jsPDF
import type jsPDF from 'jspdf';

// CDN URLs for CJK fonts
const NOTO_SANS_SC_URL = 'https://cdn.jsdelivr.net/fontsource/fonts/noto-sans-sc@latest/chinese-simplified-400-normal.ttf';
const NOTO_SANS_KR_URL = 'https://cdn.jsdelivr.net/fontsource/fonts/noto-sans-kr@latest/korean-400-normal.ttf';

interface FontState {
  fontData: string | null; // cached base64 font data
  loadPromise: Promise<ArrayBuffer> | null;
}

const fontState: Record<string, FontState> = {
  NotoSansSC: { fontData: null, loadPromise: null },
  NotoSansKR: { fontData: null, loadPromise: null },
};

// Check if text contains Korean Hangul characters
export const containsKorean = (text: string): boolean => {
  // Hangul Syllables, Hangul Jamo, Hangul Compatibility Jamo
  return /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/.test(text);
};

// Check if text contains CJK characters (Chinese/Japanese/Korean)
export const containsCJK = (text: string): boolean => {
  return /[\u4E00-\u9FFF\u3400-\u4DBF\u3000-\u303F\uFF00-\uFFEF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/.test(text);
};

// Get the appropriate font name for a given text
export const getCJKFontName = (text: string): string => {
  if (containsKorean(text)) return 'NotoSansKR';
  if (containsCJK(text)) return 'NotoSansSC';
  return 'helvetica';
};

const loadAndRegisterFont = async (
  doc: jsPDF,
  fontName: string,
  url: string,
  fileName: string
): Promise<void> => {
  const state = fontState[fontName];

  // If we already have the font data cached, just register it on this doc
  if (state.fontData) {
    doc.addFileToVFS(fileName, state.fontData);
    doc.addFont(fileName, fontName, 'normal');
    doc.setFont(fontName, 'normal');
    return;
  }

  if (!state.loadPromise) {
    state.loadPromise = fetch(url).then(res => res.arrayBuffer());
  }

  try {
    const arrayBuffer = await state.loadPromise;

    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ''
      )
    );

    state.fontData = base64;

    doc.addFileToVFS(fileName, base64);
    doc.addFont(fileName, fontName, 'normal');
    doc.setFont(fontName, 'normal');
  } catch (error) {
    // Clear cached promise so retry is possible after transient failures
    state.loadPromise = null;
    console.error(`Failed to load ${fontName} font:`, error);
    throw error;
  }
};

// Load CJK fonts based on what's needed
export const loadCJKFont = async (doc: jsPDF, needsKorean: boolean = false): Promise<void> => {
  const promises: Promise<void>[] = [];

  if (needsKorean) {
    promises.push(loadAndRegisterFont(doc, 'NotoSansKR', NOTO_SANS_KR_URL, 'NotoSansKR-Regular.ttf'));
  }

  // Always load SC for non-Korean CJK (Chinese/Japanese)
  promises.push(loadAndRegisterFont(doc, 'NotoSansSC', NOTO_SANS_SC_URL, 'NotoSansSC-Regular.ttf'));

  await Promise.all(promises);
};
